import React from "react";
import { Dropdown, DropdownItem } from "staysure-component-library";

import InputValidationWrapper from "@/components/atoms/input-validation-wrapper/input-validation-wrapper";
import { getStrokeColor } from "@/utils/organiser.utils";
import { smoothScrollToRef } from "@/utils/scrollUtils";

interface NationalityFieldProps {
  nationalityStatus: "success" | "error" | "warning" | null;
  errorDetailsFirstError: string | null;
  firstErrorRef: React.RefObject<HTMLElement | null>;
  nationalityRef: React.RefObject<HTMLDivElement | null>;
  isNationalityFocused: boolean;
  setIsNationalityFocused: (value: boolean) => void;
  isTabPressed: boolean;
  nationalityData: { id: number | string; name: string }[];
  nationalityId: string | number | undefined;
  nationalityPlaceholder?: string;
  brand: "staysure" | "avanti" | undefined;
  handleInputChange: (field: string, value: string) => void;
  setFocusedInputs: (fields: string[]) => void;
  getFocusedFields: (field: string) => string[];
  hasMounted: React.MutableRefObject<boolean>;
  isdesktop: boolean;
  setActiveHoverd: (value: boolean) => void;
  setInputDisable: (value: boolean) => void;
  Styles: Record<string, string>;
  childRef: React.MutableRefObject<HTMLDivElement | null> | undefined;
  firstNameRef: React.RefObject<HTMLElement | null>;
}

export default function NationalityField({
  nationalityStatus,
  nationalityRef,
  isNationalityFocused,
  setIsNationalityFocused,
  isTabPressed,
  nationalityData,
  nationalityId,
  nationalityPlaceholder,
  brand,
  handleInputChange,
  setFocusedInputs,
  getFocusedFields,
  hasMounted,
  isdesktop,
  setActiveHoverd,
  setInputDisable,
  Styles,
  childRef,
  firstNameRef,
}: Readonly<NationalityFieldProps>) {
  return (
    <InputValidationWrapper
      className="flex flex-row mb-spacing-s"
      status={nationalityStatus}
      childRef={childRef}
    >
      <div
        className={`${Styles.country_wrapper} ${Styles.title_dropdown} ${
          isNationalityFocused && Styles.dropdown_focused
        } flex flex-row items-start w-full h-[56px]`}
        onFocus={() => isTabPressed && setIsNationalityFocused(true)}
        onBlur={() => setIsNationalityFocused(false)}
        ref={nationalityRef}
        role="tab"
        tabIndex={-1}
      >
        <Dropdown
          id="nationality-dropdown"
          defaultValue={
            nationalityData.find((n) => n.id === nationalityId)?.name ??
            undefined
          }
          placeholderTextAndLabel={nationalityPlaceholder ?? "Nationality"}
          strokeColor={getStrokeColor(brand)}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onSelectionChange={(e: any) => {
            handleInputChange("nationalityValue", e);

            const selectedNationality = nationalityData.find(
              (n) => n.name === e
            )?.name;
            const currentNationality = nationalityData.find(
              (n) => n.id === nationalityId
            )?.name;

            if (hasMounted.current) {
              if (
                !currentNationality ||
                selectedNationality !== currentNationality
              ) {
                smoothScrollToRef(firstNameRef, 100);
              }
            } else {
              hasMounted.current = true;
            }

            if (isdesktop) {
              setActiveHoverd(false);
              setInputDisable(true);
              setTimeout(() => setInputDisable(false), 1);
              setTimeout(() => setActiveHoverd(true), 1200);
            } else {
              setActiveHoverd(false);
              setInputDisable(true);
              setTimeout(() => setInputDisable(false), 1);
            }
          }}
          onDropdownBlur={() => {
            if (!nationalityStatus) {
              setFocusedInputs(getFocusedFields("nationalityValue"));
            }
          }}
          className={`${Styles.droplistBox} ${
            nationalityStatus === "warning" ? Styles.warningMsgClass : ""
          } ${
            nationalityStatus === "success" ? Styles.successMsgClass : ""
          } border-interactive-secondary-default rounded-radius-lg max-h-spacing-xxxl`}
          placeholderLeftBeforeSelect={0}
          placeholderLeftAfterSelect={-24}
          listBoxStyle={{
            outline: "none",
            margin: "0",
            minWidth: "unset !important",
            maxHeight: "400px",
            boxShadow: "0px 0px 12px 0px rgba(15, 23, 42, 0.11)",
          }}
          listClassName={`${Styles.listStyle} mt-[10px] rounded-radius-s`}
          unorderedListClassName="w-full !mt-[3px] !md:ml-0 !ml-[-1px] !border-border-subdued !rounded-radius-lg !px-spacing-m !py-spacing-xs"
        >
          {nationalityData.map((nationality) => (
            <DropdownItem value={nationality.name} key={nationality.id}>
              <span className="w-full leading-[24px] font-proximanova text-2xs font-semibold p-spacing-xs country-name">
                {nationality.name}
              </span>
            </DropdownItem>
          ))}
        </Dropdown>
      </div>
    </InputValidationWrapper>
  );
}
